import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      const redirect = params.get("redirect");
      if (redirect) nav(redirect);
      else if (u.role === "admin") nav("/admin");
      else if (u.role === "vendor") nav(u.vendor_id ? "/vendor" : "/vendor/register");
      else if (u.role === "practitioner") nav("/practitioner/dashboard");
      else nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your Healthion account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Input label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
        <Input label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
        {error && <p className="text-xs text-red-600" data-testid="login-error">{error}</p>}
        <button className="w-full btn-forest" data-testid="login-submit">Sign In</button>
        <GoogleSignInButton />
        <p className="text-xs text-center text-[#5A6B5D]">
          New here? <Link to="/signup" className="text-[#1E3A2B] font-semibold">Create an account</Link>
        </p>
        <div className="mt-4 text-[10px] font-mono text-[#5A6B5D] border-t border-[#E2DDD5] pt-3">
          Demo · customer@healthion.in / Customer@2026 · vendor@keraliya.in / Vendor@2026
        </div>
      </form>
    </AuthShell>
  );
}

export function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("customer");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const u = await register({ name, email, password, role });
      toast.success(`Welcome to Healthion, ${u.name}`);
      if (role === "vendor") nav("/vendor/agreement");
      else if (role === "practitioner") nav("/practitioner/agreement");
      else nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Signup failed");
    }
  };

  return (
    <AuthShell title="Join Healthion India" subtitle="A curated Ayurveda marketplace by Amiroop Healthion LLP">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Input label="Full name" value={name} onChange={setName} testid="signup-name" />
        <Input label="Email" type="email" value={email} onChange={setEmail} testid="signup-email" />
        <Input label="Password" type="password" value={password} onChange={setPassword} testid="signup-password" />
        <div>
          <label className="text-xs font-mono uppercase text-[#5A6B5D]">I want to</label>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {[
              { v: "customer", l: "Shop" },
              { v: "vendor", l: "Sell products" },
              { v: "practitioner", l: "Practise" },
            ].map((r) => (
              <button
                type="button"
                key={r.v}
                onClick={() => setRole(r.v)}
                data-testid={`role-${r.v}`}
                className={`p-3 text-xs rounded-lg border font-semibold ${role === r.v ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E2DDD5] text-[#1A201C]"}`}
              >
                {r.l}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="text-xs text-red-600" data-testid="signup-error">{error}</p>}
        <button className="w-full btn-forest" data-testid="signup-submit">Create Account</button>
        <GoogleSignInButton label="Sign up with Google" />
        <p className="text-xs text-center text-[#5A6B5D]">
          Already have an account? <Link to="/login" className="text-[#1E3A2B] font-semibold">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase text-[#5A6B5D]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        data-testid={testid}
        className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm bg-white"
      />
    </div>
  );
}

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Link to="/" className="flex items-center gap-3 justify-center mb-6">
        <img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_wellness-hub-2272/artifacts/opuacvgq_amiroop.PNG.png"
          alt="Amiroop Healthion"
          className="w-14 h-14 object-contain"
          style={{ mixBlendMode: "multiply" }}
        />
        <div className="font-serif text-xl font-bold text-[#1E3A2B]">Healthion India</div>
      </Link>
      <div className="bg-white border border-[#E2DDD5] rounded-2xl p-6">
        <h1 className="font-serif text-2xl font-bold text-[#1A201C]">{title}</h1>
        <p className="text-sm text-[#5A6B5D] mt-1 mb-5">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}
